import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import _ from 'lodash';
import moment from 'moment';
import { Observable, combineLatest, iif, interval, of } from 'rxjs';
import { map, shareReplay, skipWhile, switchMap } from 'rxjs/operators';
import { AgentDetailsBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/agent-details-behavior/agent-details-behavior.state';
import { SamwinAgentListState } from 'src/app/store/samwin/samwin-agent-list/samwin-agent-list.state';
import { SamwinUserStatusSelectors } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.selectors';
import { SamwinUserStatusState } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.state';
import { SessionState } from 'src/app/store/session/session.state';
import { UserStatusInfoRemoteDto } from 'src/generated/swagger';

@Component({
   selector: 'samweb-user-status-progressbar',
   templateUrl: './user-status-progressbar.component.html',
   styleUrls: ['./user-status-progressbar.component.scss'],
})
export class UserStatusProgressbarComponent implements OnInit, OnChanges {
   @Select(SamwinUserStatusState.getNextAutoStatusChangeTime)
   public autoStatusChangeTime$: Observable<moment.Moment>;

   public currentUserStatusInfo$: Observable<UserStatusInfoRemoteDto>;
   public followUpStatusInfos$: Observable<[UserStatusInfoRemoteDto, UserStatusInfoRemoteDto[]]>;
   public percentUntilAutoChange$: Observable<number>;
   public widthForUserStatusChange$: Observable<string>;

   @Input()
   public userId: number = null;

   @Input()
   public showProgressbar = true;

   constructor(private store: Store) {
      // nothing to do
   }

   private forLoggedOnUser() {
      return _.isNil(this.userId) || this.userId === this.store.selectSnapshot(SessionState.getAuthenticatedUser).userId;
   }

   ngOnChanges(changes: SimpleChanges) {
      if (changes.userId.previousValue !== changes.userId.currentValue) {
         this.ngOnInit();
      }
   }

   ngOnInit() {
      // not available when user is different to the logged-on agent
      if (this.forLoggedOnUser()) {
         this.percentUntilAutoChange$ = this.autoStatusChangeTime$.pipe(
            map((autoStatusChangeTime) => {
               return {
                  switchedAt: moment(),
                  autoChangeAt: autoStatusChangeTime,
               };
            }),
            switchMap((changedContainer) =>
               iif(
                  () => changedContainer.autoChangeAt.isBefore(moment()),
                  of(0),
                  interval(100).pipe(
                     map(() => {
                        const currentTime = moment();
                        const currentTimeUntilChange = currentTime.diff(changedContainer.switchedAt, 'milliseconds');
                        const totalTimeUntilChange = changedContainer.autoChangeAt.diff(changedContainer.switchedAt, 'milliseconds');
                        if (totalTimeUntilChange <= 0) {
                           return 100;
                        }
                        return (100 / totalTimeUntilChange) * currentTimeUntilChange;
                     }),
                     map((x) => 100 - x)
                  )
               )
            ),
            shareReplay()
         );

         this.widthForUserStatusChange$ = this.percentUntilAutoChange$.pipe(map((x) => `${x}%`));
      }

      if (this.forLoggedOnUser()) {
         this.currentUserStatusInfo$ = this.store.select(SamwinUserStatusState.currentStatusInfo).pipe(skipWhile((value) => _(value).isNil()));

         this.followUpStatusInfos$ = combineLatest([this.currentUserStatusInfo$, this.store.select(SamwinUserStatusSelectors.getAllFollowUpStatusInfos)]);
      } else {
         this.currentUserStatusInfo$ = this.store
            .select(SamwinAgentListState.getCurrentStatusInfoFn)
            .pipe(map((getCurrentStatusInfoFn) => getCurrentStatusInfoFn(this.userId)));

         this.followUpStatusInfos$ = combineLatest([
            this.currentUserStatusInfo$,
            this.store.select(AgentDetailsBehaviourState.getAllFollowUpStatusInfosForSelectedUser),
         ]);
      }
   }
}
